'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Copy, Check, LogIn, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface LoginFields {
  email: string;
  password: string;
  remember: boolean;
}

const demoAccounts = [
  { id: 'demo-free', role: 'Free User', email: 'trader@tradewithkrish.in', password: 'TWK@Free2026' },
  { id: 'demo-pro', role: 'Pro Trader', email: 'pro.trader@tradewithkrish.in', password: 'TWK@Pro2026' },
];

export default function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    setError,
  } = useForm<LoginFields>({ defaultValues: { remember: false } });

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const autofill = (account: typeof demoAccounts[0]) => {
    setValue('email', account.email);
    setValue('password', account.password);
    toast.success(`Filled credentials for ${account.role}`);
  };

  // Backend integration point: POST /api/auth/login with email + password
  const onSubmit = async (data: LoginFields) => {
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 1400));
    setIsLoading(false);

    const valid = demoAccounts.some(
      (a) => a.email === data.email && a.password === data.password
    );

    if (!valid) {
      setError('email', {
        message: 'Invalid credentials — use the demo accounts below to sign in',
      });
      return;
    }

    toast.success('Welcome back! Redirecting to dashboard…');
    setTimeout(() => {
      window.location.href = '/';
    }, 1000);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold mb-1">Welcome back</h2>
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          Sign in to access your trade ideas and market research.
        </p>
      </div>

      {/* Email */}
      <div className="space-y-1.5">
        <label htmlFor="login-email" className="block text-sm font-medium">
          Email Address
        </label>
        <input
          id="login-email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          className="w-full px-3 py-2.5 rounded-lg text-sm transition-all duration-150 outline-none"
          style={{
            background: 'var(--input)',
            border: `1px solid ${errors.email ? 'var(--negative)' : 'var(--border)'}`,
            color: 'var(--foreground)',
          }}
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
          })}
        />
        {errors.email && (
          <p className="text-xs text-negative mt-1">{errors.email.message}</p>
        )}
      </div>

      {/* Password */}
      <div className="space-y-1.5">
        <label htmlFor="login-password" className="block text-sm font-medium">
          Password
        </label>
        <div className="relative">
          <input
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="Enter your password"
            className="w-full px-3 py-2.5 pr-10 rounded-lg text-sm transition-all duration-150 outline-none"
            style={{
              background: 'var(--input)',
              border: `1px solid ${errors.password ? 'var(--negative)' : 'var(--border)'}`,
              color: 'var(--foreground)',
            }}
            {...register('password', { required: 'Password is required' })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors duration-150"
            style={{ color: 'var(--muted-foreground)' }}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.password && (
          <p className="text-xs text-negative mt-1">{errors.password.message}</p>
        )}
      </div>

      {/* Remember + Forgot */}
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            className="w-4 h-4 rounded accent-primary"
            {...register('remember')}
          />
          <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
            Remember me
          </span>
        </label>
        <button
          type="button"
          className="text-sm transition-colors duration-150 hover:text-primary"
          style={{ color: 'var(--accent)' }}
        >
          Forgot password?
        </button>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-150 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
        style={{ background: 'var(--primary)', color: 'var(--primary-foreground)', minHeight: '42px' }}
      >
        {isLoading ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <>
            <LogIn size={16} />
            Sign In
          </>
        )}
      </button>

      {/* Demo Credentials Box */}
      <div
        className="rounded-xl p-4 space-y-3"
        style={{ background: 'var(--surface-3)', border: '1px solid var(--border)' }}
      >
        <p className="text-xs font-semibold" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.04em' }}>
          DEMO ACCOUNTS — Click to autofill
        </p>
        <div className="space-y-2">
          {demoAccounts.map((account) => (
            <div
              key={account.id}
              className="rounded-lg p-3 space-y-2"
              style={{ background: 'var(--input)', border: '1px solid var(--border)' }}
            >
              <div className="flex items-center justify-between">
                <span
                  className="text-xs font-semibold"
                  style={{ color: 'var(--primary)' }}
                >
                  {account.role}
                </span>
                <button
                  type="button"
                  onClick={() => autofill(account)}
                  className="text-xs px-2 py-0.5 rounded transition-all duration-150 hover:opacity-80 active:scale-95"
                  style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
                >
                  Use
                </button>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-mono-nums truncate" style={{ color: 'var(--muted-foreground)' }}>
                    {account.email}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleCopy(account.email, `${account.id}-email`)}
                    className="flex-shrink-0 transition-colors duration-150"
                    style={{ color: 'var(--muted-foreground)' }}
                    aria-label="Copy email"
                  >
                    {copiedField === `${account.id}-email` ? (
                      <Check size={12} className="text-positive" />
                    ) : (
                      <Copy size={12} />
                    )}
                  </button>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-mono-nums" style={{ color: 'var(--muted-foreground)' }}>
                    {account.password}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleCopy(account.password, `${account.id}-pass`)}
                    className="flex-shrink-0 transition-colors duration-150"
                    style={{ color: 'var(--muted-foreground)' }}
                    aria-label="Copy password"
                  >
                    {copiedField === `${account.id}-pass` ? (
                      <Check size={12} className="text-positive" />
                    ) : (
                      <Copy size={12} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </form>
  );
}